import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Building2, Users, Trophy, DollarSign, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeagueReportsPanel({ leagueId }) {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/league/${leagueId}/aggregation`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [leagueId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  if (!data) return (
    <Card className={bg}>
      <CardContent className="py-8 text-center">
        <BarChart3 className={`h-10 w-10 mx-auto mb-3 ${muted}`} />
        <p className={muted}>No league data available.</p>
      </CardContent>
    </Card>
  );

  const statCards = [
    { label: 'Organizations', value: data.organizations, icon: Building2, color: 'text-blue-500' },
    { label: 'Total Teams', value: data.total_teams, icon: Trophy, color: 'text-amber-500' },
    { label: 'Total Players', value: data.total_players, icon: Users, color: 'text-emerald-500' },
    { label: 'Total Families', value: data.total_families, icon: Users, color: 'text-purple-500' },
    { label: 'Total Revenue', value: `$${data.total_revenue.toLocaleString()}`, icon: DollarSign, color: 'text-green-500' },
    { label: 'Total Games', value: data.total_games, icon: BarChart3, color: 'text-cyan-500' },
  ];

  return (
    <div className="space-y-6" data-testid="league-reports-panel">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-amber-500" />
        <h2 className={`text-lg font-semibold ${text}`}>League Aggregation</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {statCards.map(s => (
          <Card key={s.label} className={bg}>
            <CardContent className="py-4 px-4 flex items-center gap-3">
              <s.icon className={`h-8 w-8 ${s.color}`} />
              <div>
                <p className={`text-xs ${muted}`}>{s.label}</p>
                <p className={`text-xl font-bold ${text}`}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.org_summaries?.length > 0 && (
        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>Organization Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="org-breakdown-table">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <th className={`text-left py-2 px-2 font-medium ${muted}`}>Organization</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Teams</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Players</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>M/F</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Families</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Coaches</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Revenue</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Games</th>
                  </tr>
                </thead>
                <tbody>
                  {data.org_summaries.map(o => (
                    <tr key={o.org_id} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}>
                      <td className={`py-2 px-2 font-medium ${text}`}>{o.org_name}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{o.teams}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{o.players}</td>
                      <td className="py-2 px-1 text-center">
                        <Badge variant="outline" className="text-xs">{o.males}M / {o.females}F</Badge>
                      </td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{o.families}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{o.coaches}</td>
                      <td className="py-2 px-1 text-center text-green-500 font-medium">${o.revenue.toLocaleString()}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{o.games}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
